// Site Check client-side data layer. Thin async wrappers around the
// /sitecheck/* HTTP endpoints. No localStorage, no IndexedDB —
// everything lives on the backend (Cosmos for templates / sites /
// state, Azure Blob Storage for photos and voice notes). The wizard
// drives loads on mount and saves through the same path on every
// change.

import { Template, Site, SiteState, PhotoEntry } from "./types";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─── Templates ────────────────────────────────────────────────────────────

export async function loadTemplates(): Promise<Template[]> {
  const r = await fetch(`${BACKEND}/sitecheck/templates`, {
    credentials: "include",
  });
  if (!r.ok) throw new Error(`Failed to load templates: ${r.status}`);
  const body = await r.json();
  return Array.isArray(body.templates) ? body.templates : [];
}

export async function saveTemplates(templates: Template[]): Promise<void> {
  const r = await fetch(`${BACKEND}/sitecheck/templates`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ templates }),
  });
  if (!r.ok) throw new Error(`Failed to save templates: ${r.status}`);
}

// ─── Sites ────────────────────────────────────────────────────────────────

export async function loadSites(): Promise<Site[]> {
  const r = await fetch(`${BACKEND}/sitecheck/sites`, {
    credentials: "include",
  });
  if (!r.ok) throw new Error(`Failed to load sites: ${r.status}`);
  const body = await r.json();
  return Array.isArray(body.sites) ? body.sites : [];
}

export async function saveSites(sites: Site[]): Promise<void> {
  const r = await fetch(`${BACKEND}/sitecheck/sites`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sites }),
  });
  if (!r.ok) throw new Error(`Failed to save sites: ${r.status}`);
}

// ─── Per-site state (answers + submitted) ─────────────────────────────────

export async function loadSiteState(siteId: string): Promise<SiteState> {
  const r = await fetch(
    `${BACKEND}/sitecheck/site-state/${encodeURIComponent(siteId)}`,
    { credentials: "include" },
  );
  if (!r.ok) throw new Error(`Failed to load site state: ${r.status}`);
  const body = await r.json();
  return {
    siteId,
    answers: body.answers || {},
    submitted: body.submitted || {},
    updatedAt: body.updatedAt || new Date().toISOString(),
  };
}

export async function saveSiteState(state: SiteState): Promise<void> {
  const r = await fetch(
    `${BACKEND}/sitecheck/site-state/${encodeURIComponent(state.siteId)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        answers: state.answers,
        submitted: state.submitted,
      }),
    },
  );
  if (!r.ok) throw new Error(`Failed to save site state: ${r.status}`);
}

// ─── Files (photos + voice notes) ─────────────────────────────────────────

export interface UploadResult {
  name: string;
  type: string;
  blobName: string;
  uploadedAt: string;
}

export async function uploadFile(
  siteId: string,
  fieldKey: string,
  file: File | Blob,
  fileName: string,
): Promise<UploadResult> {
  const fd = new FormData();
  // Wrap raw Blobs in a File so multer sees a usable originalname
  // (the blob path is built from it). Already-File objects pass through.
  const named =
    file instanceof File
      ? file
      : new File([file], fileName, { type: file.type || "application/octet-stream" });
  fd.append("file", named, fileName);
  const r = await fetch(
    `${BACKEND}/sitecheck/files/${encodeURIComponent(siteId)}/${encodeURIComponent(fieldKey)}`,
    {
      method: "POST",
      credentials: "include",
      body: fd,
    },
  );
  if (!r.ok) throw new Error(`Upload failed: ${r.status}`);
  return r.json();
}

// Returns a base64 data URL for the blob, streamed through the backend
// proxy (?format=base64). Used for audio playback — new Audio(dataUrl)
// tolerates data: URLs — and never points the browser at the storage
// account directly (required now that Blob is firewalled to backend IPs).
export async function getFileUrl(
  siteId: string,
  fieldKey: string,
  blobName: string,
): Promise<string> {
  const { base64, contentType } = await fetchFileBase64(
    siteId,
    fieldKey,
    blobName,
  );
  return `data:${contentType};base64,${base64}`;
}

// Fetches blob bytes via the backend proxy and returns a revocable object
// URL suitable for <img src>. The caller MUST URL.revokeObjectURL(url) when
// done (e.g. effect cleanup). Mirrors the SMRT COP photo-preview pattern.
export async function getFileObjectUrl(
  siteId: string,
  fieldKey: string,
  blobName: string,
): Promise<string> {
  const { base64, contentType } = await fetchFileBase64(
    siteId,
    fieldKey,
    blobName,
  );
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], {
    type: contentType || "application/octet-stream",
  });
  return URL.createObjectURL(blob);
}

// Single proxied read: GET /sitecheck/files/:siteId/:fieldKey?blobName=..&format=base64.
// The backend streams the blob to us (its IPs are allowlisted), so the
// browser never touches *.blob.core.windows.net.
async function fetchFileBase64(
  siteId: string,
  fieldKey: string,
  blobName: string,
): Promise<{ base64: string; contentType: string }> {
  const qs = new URLSearchParams({ blobName, format: "base64" });
  const r = await fetch(
    `${BACKEND}/sitecheck/files/${encodeURIComponent(siteId)}/${encodeURIComponent(fieldKey)}?${qs.toString()}`,
    { credentials: "include" },
  );
  if (!r.ok) throw new Error(`Failed to get file: ${r.status}`);
  const body = await r.json();
  if (!body.base64 || !body.contentType) {
    throw new Error("File response missing base64/contentType");
  }
  return {
    base64: body.base64 as string,
    contentType: body.contentType as string,
  };
}

export async function deleteFile(
  siteId: string,
  fieldKey: string,
  blobName: string,
): Promise<void> {
  const qs = new URLSearchParams({ blobName });
  const r = await fetch(
    `${BACKEND}/sitecheck/files/${encodeURIComponent(siteId)}/${encodeURIComponent(fieldKey)}?${qs.toString()}`,
    { method: "DELETE", credentials: "include" },
  );
  if (!r.ok) throw new Error(`Delete failed: ${r.status}`);
}

// ─── Helpers ──────────────────────────────────────────────────────────────

export function newId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// Best-effort geolocation. Resolves with null on denial / timeout so
// callers can attach a photo even when location is unavailable. 15 s
// timeout because cold-start GPS lock on iPhone (no recent fix) can take
// 10-12 s — a tighter window left too many photos with no geo tag.
// accuracy comes straight from the browser's Geolocation API in metres.
//
// iOS WKWebView race: when called immediately after a UIImagePickerController
// (HTML file input with capture="environment") dismisses, getCurrentPosition
// can trigger a system alert (permission prompt or "continue allowing
// location" recap) that iOS tries to present on the still-mid-dismissal
// picker. That throws "view is not in the window hierarchy" and the success
// /error callbacks never fire, leaving the promise hanging forever.
//
// Two safeguards:
//   1. PRE_GEO_DELAY_MS: wait for the picker to fully dismiss before invoking
//      the geolocation API. 400ms covers iOS's dismissal animation.
//   2. HARD_TIMEOUT_MS: backstop in case the API still hangs (the built-in
//      `timeout` option doesn't fire when no callback path runs at all).
export function captureGeo(): Promise<{
  lat: number;
  lng: number;
  accuracy?: number;
} | null> {
  const PRE_GEO_DELAY_MS = 400;
  const HARD_TIMEOUT_MS = 16_000;
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    let settled = false;
    const settle = (value: { lat: number; lng: number; accuracy?: number } | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const hardTimer = setTimeout(() => settle(null), HARD_TIMEOUT_MS);
    setTimeout(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(hardTimer);
          settle({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
        },
        () => {
          clearTimeout(hardTimer);
          settle(null);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
      );
    }, PRE_GEO_DELAY_MS);
  });
}

// Stub for the eventual section-complete push. Currently logs only;
// promote to a real notificationService call when crew-lead pushes
// for completion are wanted.
export function notifySectionCompletedStub(args: {
  siteId: string;
  siteName: string;
  sectionLabel: string;
  completerUserId: string;
}): void {
  console.log("[SiteCheck] section complete →", args);
}

// Photo answer accessor — keeps the cast to PhotoEntry[] in one place
// so callers don't sprinkle `as` assertions through component code.
export function getPhotoAnswers(
  state: SiteState,
  questionKey: string,
): PhotoEntry[] {
  const v = state.answers[questionKey];
  return Array.isArray(v) ? (v as PhotoEntry[]) : [];
}
